import { observer } from 'mobx-react';
import React from 'react';
import { MODIFIER_COLUMN } from '../constants/itemBranchesModifier';
import itemBranchesModifierStore from '../store/itemBranchesModifierStore';
import { IItemBranchesModifier, IItemBranchesModifierOption } from '../types/itemBranchesModifier';
import EditPrice from '../../../components/EditPrice/EditPrice';
import SetToDefaultButton from '../../../components/SetToDefaultButton/SetToDefaultButton';
import VatLabel, { VatType } from '../../../components/VatLabel/VatLabel';
import { booleanToNumber } from '~/utils/helpers';
import Switcher from '~/view/components/Controls/Switcher/Switcer';
import IntegrationManager from '~/view/components/IntegrationManager/IntegrationManager';
import { branchOptionAvailabilityRestriction, branchOptionPriceRestriction } from '~/services/restriction';

export interface IRowProps {
  modifier: IItemBranchesModifier;
  option: IItemBranchesModifierOption;
  isFirst: boolean;
}

const ModifierOptionRow: React.FC<IRowProps> = ({ modifier, option, isFirst }: IRowProps): JSX.Element | null => {
  const defaultClasses = 'family-medium text-size-16';

  // With Size
  const changeOptionPrice = (field: keyof IItemBranchesModifierOption) => (price: number | string) => {
    itemBranchesModifierStore.changeOptionHandler(modifier.branch_id, option.option_id, { [field]: price });
  };

  const changeOptionAvailability = (available: number): void => {
    itemBranchesModifierStore.changeOptionHandler(modifier.branch_id, option.option_id, { branch_option_available: Boolean(available) });
  };

  const setOptionDefaultPrice = (): void => {
    if (option.branch_option_price === option.modifier_option_price) {
      return;
    }

    itemBranchesModifierStore.changeOptionHandler(modifier.branch_id, option.option_id, {
      branch_option_price: option.modifier_option_price,
    });
  };

  return (
    <div className="item-branches-modifiers-row">
      <div className={`cell cell-${MODIFIER_COLUMN.BRANCH}`}>
        {isFirst ? (
          <>
            <p className={defaultClasses}>{modifier.branch_name}</p>
            <VatLabel type={VatType.INCLUDED_OR_EXCLUDED_VAT} isVat={modifier.franchise_vat_included} />
          </>
        ) : null}
      </div>

      <div className={`cell cell-${MODIFIER_COLUMN.OPTION_NAME}`}>
        <p className={defaultClasses}>{option.modifier_option_name}</p>
        <VatLabel type={VatType.WITH_OR_WITHOUT_VAT} isVat={option.option_vat} />
      </div>

      <div className={`cell cell-${MODIFIER_COLUMN.PRICE}`}>
        <IntegrationManager setting={branchOptionPriceRestriction(modifier.branch_pos_service_id)} withTooltip>
          <EditPrice
            value={option.branch_option_price}
            currency={modifier.franchise_currency}
            onChangePrice={changeOptionPrice('branch_option_price')}
          />
        </IntegrationManager>
      </div>

      <div className={`cell cell-${MODIFIER_COLUMN.DEFAULT_PRICE}`}>
        <EditPrice
          value={option.modifier_option_price}
          currency={modifier.default_currency}
          disabled={true}
          onChangePrice={changeOptionPrice('modifier_option_price')}
        />
        {Number(option.branch_option_price) !== Number(option.modifier_option_price) ? (
          <IntegrationManager setting={branchOptionPriceRestriction(modifier.branch_pos_service_id)} withTooltip>
            <SetToDefaultButton onClick={setOptionDefaultPrice} />
          </IntegrationManager>
        ) : null}
      </div>

      <div className={`cell cell-${MODIFIER_COLUMN.AVAILABILITY}`}>
        <IntegrationManager setting={branchOptionAvailabilityRestriction(modifier.branch_pos_service_id)} withTooltip>
          <Switcher status={booleanToNumber(option.branch_option_available)} onChange={changeOptionAvailability} activeClassName="bg-mainblue" />
        </IntegrationManager>
      </div>
    </div>
  );
};

export default observer(ModifierOptionRow);
